import { LoadingSpinnerAnimation } from 'assets/animations';
import { OutlinedInfoIcon, WhiteInfoIcon } from 'assets/icons';
import classnames from 'classnames';
import { NotifierTypes } from 'constants/notifierConstants';
import { getNotifications, showNotifier } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import Lottie from 'lottie-react';
import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { VoidFn } from 'types/baseTypes';

export enum ButtonType {
  SECONDARY = 'SECONDARY',
  PRIMARY = 'PRIMARY',
  TERTIARY = 'TERTIARY',
  ALERT = 'ALERT',
  GHOST = 'GHOST'
}

export enum FormButtonType {
  submit = 'submit',
  button = 'button',
  reset = 'reset'
}

interface ButtonProps {
  id: string;
  children?: ReactNode;
  StartIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string; id?: string }> | null;
  startIconId?: string;
  EndIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }>;
  className?: string;
  type?: ButtonType;
  formType?: FormButtonType;
  disabled?: boolean;
  showDisabledReason?: boolean;
  onClick?: VoidFn;
  primaryButtonColor?: string;
  childrenContainerStyles?: string;
  isLoading?: boolean;
  startIconStyle?: string;
  endIconStyle?: string;
  disabledReason?: string;
  customLottieClass?: string;
}

/*This component will act as the primary button by default*/
const Button: FC<ButtonProps> = ({
  id,
  disabled = false,
  showDisabledReason = false,
  children,
  StartIcon,
  startIconId,
  EndIcon,
  className,
  type = ButtonType.PRIMARY,
  onClick = null,
  formType = FormButtonType.button,
  childrenContainerStyles = '',
  isLoading = false,
  startIconStyle = '',
  endIconStyle = '',
  disabledReason,
  customLottieClass = ''
}) => {
  const isSecondaryBtn = type === ButtonType.SECONDARY;
  const isPrimaryBtn = type === ButtonType.PRIMARY;
  const isTertiaryBtn = type === ButtonType.TERTIARY;
  const isAlert = type === ButtonType.ALERT;
  const isGhostBtn = type === ButtonType.GHOST;

  const notifications = useAppSelector(getNotifications);
  const dispatch = useAppDispatch();

  const onInfoClick = () => {
    const existingNotification = notifications.find(notification => notification.id === id);
    if (existingNotification) return;
    dispatch(
      showNotifier({
        id,
        message: {
          primaryMessage: disabledReason,
          secondaryMessage: '',
          isMessageHtml: true
        },
        type: NotifierTypes.WARNING,
        showClose: true,
        fontStyle: 'text-primary font-normal'
      })
    );
  };

  const handleButtonClick = () => {
    if (disabled) {
      return;
    }
    if (showDisabledReason && disabledReason) {
      onInfoClick();
      return;
    }
    onClick?.();
  };

  return (
    <button
      id={id}
      type={formType}
      onClick={handleButtonClick}
      className={twMerge(
        classnames(
          `flex h-11 w-fit cursor-pointer flex-row items-center 
           justify-center rounded-md px-[15px] text-sbase font-semibold
           active:shadow-xl disabled:pointer-events-none disabled:cursor-not-allowed
           disabled:opacity-50`,
          {
            'border border-secondary bg-primary text-secondaryBtnText shadow-sm hover:bg-[#ECF1F7]': isSecondaryBtn,
            'bg-primaryBtn text-primaryBtnText hover:bg-primaryButtonHover': isPrimaryBtn,
            'bg-tertiaryText text-primaryBtnText active:shadow-none': isTertiaryBtn,
            'opacity:100 bg-[#9BBADD] hover:bg-[#9BBADD]': showDisabledReason && isPrimaryBtn,
            'opacity:50': showDisabledReason && isTertiaryBtn,
            'bg-error text-white hover:bg-[#C4503E]': isAlert,
            'bg-transparent text-secondaryBtnText hover:shadow-sm': isGhostBtn
          }
        ),
        className
      )}
      disabled={disabled || isLoading}>
      {!isLoading ? (
        <div className={twMerge('flex flex-row gap-2 text-sbase items-center', childrenContainerStyles)}>
          {Boolean(StartIcon) && (
            <StartIcon
              className={twMerge(
                classnames('shrink-0 ', {
                  'opacity-50': showDisabledReason
                }),
                startIconStyle
              )}
              id={startIconId}
            />
          )}
          <div
            className={twMerge(
              classnames({
                'opacity-50': showDisabledReason
              })
            )}>
            {children}
          </div>
          {Boolean(EndIcon) && <EndIcon className={twMerge(classnames('shrink-0 '), endIconStyle)} />}
          {showDisabledReason && (isPrimaryBtn || isTertiaryBtn) && <WhiteInfoIcon />}
          {showDisabledReason && isSecondaryBtn && <OutlinedInfoIcon className="opacity-50" />}
        </div>
      ) : (
        <Lottie className={`${customLottieClass} h-20 w-20`} animationData={LoadingSpinnerAnimation} loop={true} />
      )}
    </button>
  );
};

export default Button;
